import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { JSONSchema7 } from "json-schema";
import type { ConnectionProviderKey } from "@forgepoint/shared";

export interface TriggerDefinition {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  configurationSchema: JSONSchema7;
  outputSchema: JSONSchema7;
  appConnectionConfig?: { appId: string; providerId: ConnectionProviderKey };
}

interface TriggerState {
  isLoadingGet: boolean;
  getError: string | null;
}

interface AgentWorkflowTriggersStore {
  triggers: Map<string, TriggerDefinition>;
  triggerState: TriggerState;

  fetchTriggers: () => Promise<void>;
  fetchAppTriggers: (appId: string) => Promise<void>;
  fetchTriggersByIds: (triggerIds: string[]) => Promise<void>;
  getTriggers: () => Map<string, TriggerDefinition>;
  getTriggerById: (triggerId: string) => TriggerDefinition | undefined;
}

export const useAgentWorkflowTriggersStore = create<AgentWorkflowTriggersStore>(
  (set, get) => ({
    triggers: new Map(),
    triggerState: {
      isLoadingGet: false,
      getError: null,
    },

    fetchTriggers: async () => {
      set({
        triggerState: { isLoadingGet: true, getError: null },
      });

      try {
        const response = await fetchApi("apps/triggers", "GET");

        if (response.ok && response.data) {
          const data = response.data as { triggers: TriggerDefinition[] };
          const triggersMap = new Map<string, TriggerDefinition>();
          data.triggers.forEach((trigger) => {
            triggersMap.set(trigger.id, trigger);
          });
          set({
            triggers: triggersMap,
            triggerState: { isLoadingGet: false, getError: null },
          });
        } else {
          set({
            triggerState: {
              isLoadingGet: false,
              getError: "Failed to fetch triggers",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch triggers";
        captureException(error as Error);
        set({
          triggerState: { isLoadingGet: false, getError: errorMessage },
        });
      }
    },

    fetchAppTriggers: async (appId: string) => {
      set({
        triggerState: { isLoadingGet: true, getError: null },
      });

      try {
        const response = await fetchApi(`apps/${appId}/triggers`, "GET");

        if (response.ok && response.data) {
          const data = response.data as { triggers: TriggerDefinition[] };
          const currentTriggers = get().triggers;
          const updatedTriggers = new Map(currentTriggers);
          data.triggers.forEach((trigger) => {
            updatedTriggers.set(trigger.id, trigger);
          });
          set({
            triggers: updatedTriggers,
            triggerState: { isLoadingGet: false, getError: null },
          });
        } else {
          set({
            triggerState: {
              isLoadingGet: false,
              getError: "Failed to fetch app triggers",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch app triggers";
        captureException(error as Error);
        set({
          triggerState: { isLoadingGet: false, getError: errorMessage },
        });
      }
    },

    fetchTriggersByIds: async (triggerIds: string[]) => {
      if (!triggerIds.length) {
        return;
      }

      set({
        triggerState: { isLoadingGet: true, getError: null },
      });

      try {
        const response = await fetchApi("apps/triggers", "GET", {
          params: { triggerIds: triggerIds.join(",") },
        });

        if (response.ok && response.data) {
          const data = response.data as { triggers: TriggerDefinition[] };
          const currentTriggers = get().triggers;
          const updatedTriggers = new Map(currentTriggers);
          data.triggers.forEach((trigger) => {
            updatedTriggers.set(trigger.id, trigger);
          });
          set({
            triggers: updatedTriggers,
            triggerState: { isLoadingGet: false, getError: null },
          });
        } else {
          set({
            triggerState: {
              isLoadingGet: false,
              getError: "Failed to fetch triggers by IDs",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch triggers by IDs";
        captureException(error as Error);
        set({
          triggerState: { isLoadingGet: false, getError: errorMessage },
        });
      }
    },

    getTriggers: () => get().triggers,

    getTriggerById: (triggerId: string) => get().triggers.get(triggerId),
  }),
);
